import { Injectable } from '@angular/core';
import { TDocumentDefinitions, Content, StyleDictionary } from 'pdfmake/interfaces';
import htmlToPdfmake from 'html-to-pdfmake';
import { asBlob } from 'html-docx-js-typescript';
import { firstValueFrom } from 'rxjs';
import { Note, DataService, Category, Tag } from './data.service';
import { PdfService } from './pdf.service';
import { PdfEmojiTextBuilder } from '../_util/pdf-emoji-text-builder';
import { FileUploadService, Attachment, isAttachmentImage } from './file-upload.service';

type StructuredExportNode = StructuredHtmlNode | StructuredCollapsibleNode;

interface StructuredHtmlNode {
  type: 'html';
  html: string;
}

interface StructuredCollapsibleNode {
  type: 'collapsible';
  title: string;
  children: StructuredExportNode[];
  meta?: {
    sourceTag?: string;
  };
}

interface CollapsibleExportSelectorConfig {
  rootSelector: string;
  titleSelector: string;
  bodySelector: string;
}

interface HtmlToPdfRenderOptions {
  tableFontSize?: number;
  lineHeight?: number;
  textMargin?: [number, number, number, number];
}

interface DocxPreparedNoteContent {
  note: Note;
  noteTitle: string;
  categoryNames: string;
  tagNames: string;
  descriptionHtml: string;
  commentsHtml: string;
}

interface BulkDocxExportOptions {
  fileName?: string;
  includeTableOfContents?: boolean;
  pageBreakBetweenNotes?: boolean;
  pageNumbers?: boolean;
  includeMetaData?: boolean;
}

/** A single note's portable fields for the "Export to JSON file" feature.
 * Deliberately excludes Tags, RelatedNotes, and Attachments, and any
 * AI-generated data (ai_summary, AiKeywords) per the feature spec — this is
 * meant to be a clean, human-portable snapshot of a note's own content, not
 * a full internal dump (see NOTE_WRITABLE_FIELDS / raw IndexedDB export for
 * that). DescriptionDelta/CommentsDelta (the legacy Quill-internal format)
 * are also excluded — DescriptionHtml/CommentsHtml is the canonical,
 * portable content representation. */
export interface NoteJsonExportRecord {
  _id: string;
  type: string;
  templateName?: string;
  NoteType: string;
  Title: string;
  Project: number;
  ProjectName: string;
  Status: string;
  ReferenceDate?: string;
  StartDate?: string;
  EndDate?: string;
  Confidential: boolean;
  DescriptionHtml: string;
  CommentsHtml: string;
  Keywords: string;
  Categories?: string[];
  created_on: string;
  last_modified: string;
}

/** Envelope for the exported .json file. `notes` is already an array so a
 * future bulk-export ("2nd phase") can populate it with more than one note
 * without any format change. */
export interface NoteJsonExportFile {
  exportFormatVersion: 1;
  exportedAt: string;
  noteCount: number;
  notes: NoteJsonExportRecord[];
}

@Injectable({ providedIn: 'root' })
export class NotePrintExportService {
  private readonly builder = new PdfEmojiTextBuilder();
  private readonly pdfmakeSupportedImageMimeTypes = new Set(['image/png', 'image/jpeg', 'image/jpg']);
  private readonly cssPxToPdfPt = 72 / 96;
  // Match pdfmake list nesting closer to marker-gap sizing (roughly width of "9. ").
  private readonly checklistIndentEmPerLevel = 1.2;
  private readonly checklistIndentPdfPointsPerLevel = 14;
  private readonly checklistIndentNbspPerLevel = 4;
  private readonly checklistDepthTokenPrefix = '[[TASK_DEPTH_';
  private readonly checklistMarkerRegex = /^[\s\u00A0]*[\u2611\u2610]/;
  private readonly collapsibleExportSelectors: CollapsibleExportSelectorConfig = {
    rootSelector: '[data-type="collapsible"], .collapsible-section',
    titleSelector: '[data-type="collapsible-title"], .collapsible-title',
    bodySelector: '[data-type="collapsible-content"], .collapsible-content',
  };
  private readonly maxCollapsibleVisualDepth = 3;
  private readonly emojiStyleKeys = [
    'fontSize',
    'bold',
    'italics',
    'color',
    'background',
    'decoration',
    'decorationStyle',
    'decorationColor',
    'alignment',
    'characterSpacing',
    'opacity',
    'style',
  ];

  constructor(
    private pdfService: PdfService,
    private dataService: DataService,
    private fileUpload: FileUploadService,
  ) {}

  async printOrExportNote(
    note: Note,
    mode: 'export' | 'print' = 'export',
    options?: { includeAttachments?: boolean },
  ): Promise<void> {
    const categoryLookup = await this.loadCategoryLookup();
    const tagLookup = await this.loadTagLookup();
    const docDefinition = await this.buildDocDefinition(
      note,
      categoryLookup,
      tagLookup,
      options?.includeAttachments === true,
    );
    if (mode === 'print') {
      await this.pdfService.printFromPdfmake(docDefinition);
      return;
    }
    const filename = this.sanitizeFilename(note.Title);
    await this.pdfService.downloadFromPdfmake(docDefinition, filename);
  }

  async printOrExportNotesByIds(
    noteIds: string[],
    notes: Note[],
    mode: 'export' | 'print' = 'export',
    options?: {
      fileName?: string;
      includeTableOfContents?: boolean;
      pageBreakBetweenNotes?: boolean;
      pageNumbers?: boolean;
    },
  ): Promise<void> {
    const orderedNotes = noteIds
      .map((id) => notes.find((note) => note._id === id))
      .filter((note): note is Note => !!note);

    if (!orderedNotes.length) {
      throw new Error('No matching notes found for export.');
    }

    const categoryLookup = await this.loadCategoryLookup();
    const tagLookup = await this.loadTagLookup();
    const content: Content[] = [];
    const addPageBreak = options?.pageBreakBetweenNotes !== false;
    if (options?.includeTableOfContents) {
      content.push(...this.buildTableOfContentsContent());
    }

    for (let index = 0; index < orderedNotes.length; index++) {
      const note = orderedNotes[index];
      content.push(
        ...(await this.buildNoteContent(note, categoryLookup, {
          tagLookup,
          includeInTableOfContents: options?.includeTableOfContents === true,
        })),
      );
      if (addPageBreak && index < orderedNotes.length - 1) {
        content.push({ text: '', pageBreak: 'after' });
      }
    }

    const docDefinition: TDocumentDefinitions = {
      content,
      styles: this.getDocStyles(),
    };

    if (options?.pageNumbers) {
      docDefinition.footer = (currentPage: number, pageCount: number) => ({
        text: `${currentPage} / ${pageCount}`,
        alignment: 'center',
        fontSize: 9,
        color: '#6b7280',
        margin: [0, 6, 0, 0],
      });
    }

    if (mode === 'print') {
      await this.pdfService.printFromPdfmake(docDefinition);
      return;
    }

    const filename = this.sanitizeFilename(options?.fileName || 'notes_export.pdf');
    await this.pdfService.downloadFromPdfmake(docDefinition, filename);
  }

  async exportCollapsibleSectionToPdf(title: string, contentHtml: string): Promise<void> {
    const safeTitle = this.normalizeTitleForPdf(title);
    const contentAsPdfMake = await this.convertHtmlToPdfContentWithStructuredCollapsibles(contentHtml || '', {
      lineHeight: 1.05,
      textMargin: [0, 2, 0, 2],
    });

    const docDefinition: TDocumentDefinitions = {
      content: [
        {
          text: this.builder.make(safeTitle),
          style: 'collapsibleDocumentTitle',
        },
        {
          text: '',
          margin: [0, 0, 0, 6],
        },
        ...contentAsPdfMake,
      ],
      styles: this.getDocStyles(),
    };

    const filename = this.sanitizeFilename(safeTitle);
    await this.pdfService.downloadFromPdfmake(docDefinition, filename);
  }

  /** Exports one or more notes to a single .json file (see NoteJsonExportFile).
   * Called today with a single note from ViewComponent's "Export" menu; the
   * envelope already supports multiple notes so a future bulk-export entry
   * point can call this with more than one without a format change. */
  async exportNotesToJson(notes: Note[], fileName?: string): Promise<void> {
    if (!notes.length) {
      throw new Error('No notes to export.');
    }

    const file: NoteJsonExportFile = {
      exportFormatVersion: 1,
      exportedAt: new Date().toISOString(),
      noteCount: notes.length,
      notes: notes.map((note) => this.buildJsonExportRecord(note)),
    };

    const jsonStr = JSON.stringify(file, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const baseName = fileName || (notes.length === 1 ? notes[0].Title : 'notes_export');
    this.downloadBlob(blob, this.sanitizeFilenameForExtension(baseName, '.json'));
  }

  private buildJsonExportRecord(note: Note): NoteJsonExportRecord {
    const record: NoteJsonExportRecord = {
      _id: note._id,
      type: note.type,
      NoteType: note.NoteType,
      Title: note.Title,
      Project: note.Project,
      ProjectName: note.ProjectName,
      Status: note.Status,
      Confidential: !!note.Confidential,
      DescriptionHtml: note.DescriptionHtml ?? '',
      CommentsHtml: note.CommentsHtml ?? '',
      Keywords: note.Keywords ?? '',
      created_on: note.created_on,
      last_modified: note.last_modified,
    };
    if (note.templateName) record.templateName = note.templateName;
    if (note.ReferenceDate) record.ReferenceDate = note.ReferenceDate;
    if (note.StartDate) record.StartDate = note.StartDate;
    if (note.EndDate) record.EndDate = note.EndDate;
    if (Array.isArray(note.Categories) && note.Categories.length) {
      record.Categories = [...note.Categories];
    }
    return record;
  }

  async exportNoteToDocx(note: Note): Promise<void> {
    const categoryLookup = await this.loadCategoryLookup();
    const tagLookup = await this.loadTagLookup();
    const descriptionHtml = await this.prepareQuillHtmlContentForPdfExport(note.DescriptionHtml || '', note._id);
    const commentsHtml = await this.prepareQuillHtmlContentForPdfExport(note.CommentsHtml || '', note._id);

    const categoryNames = (note.Categories || [])
      .map((categoryId) => categoryLookup.get(categoryId) || categoryId)
      .join(', ');
    const tagNames = (note.Tags || [])
      .map((tag) => {
        if (typeof tag === 'string') {
          return tagLookup.get(tag) || tag;
        }
        return tag?.name || '';
      })
      .filter((name) => !!name)
      .join(', ');
    const noteTitle = String(note.Title || '').trim() || 'Untitled note';
    const docxHtml = this.buildDocxHtmlDocument(note, {
      noteTitle,
      categoryNames,
      tagNames,
      descriptionHtml,
      commentsHtml,
    });
    const rawDocx = await asBlob(docxHtml, {
      orientation: 'portrait',
      margins: {
        top: 720,
        right: 720,
        bottom: 720,
        left: 720,
      },
    });
    const blob = this.toBrowserBlob(rawDocx);
    const filename = this.sanitizeFilenameForExtension(noteTitle, '.docx');
    this.downloadBlob(blob, filename);
  }

  async exportNotesToDocxByIds(noteIds: string[], notes: Note[], options?: BulkDocxExportOptions): Promise<void> {
    const orderedNotes = noteIds
      .map((id) => notes.find((note) => note._id === id))
      .filter((note): note is Note => !!note);

    if (!orderedNotes.length) {
      throw new Error('No matching notes found for export.');
    }

    const categoryLookup = await this.loadCategoryLookup();
    const tagLookup = await this.loadTagLookup();
    const preparedNotes: DocxPreparedNoteContent[] = [];

    for (const note of orderedNotes) {
      const descriptionHtml = await this.prepareQuillHtmlContentForPdfExport(note.DescriptionHtml || '', note._id);
      const commentsHtml = await this.prepareQuillHtmlContentForPdfExport(note.CommentsHtml || '', note._id);

      const categoryNames = (note.Categories || [])
        .map((categoryId) => categoryLookup.get(categoryId) || categoryId)
        .join(', ');
      const tagNames = (note.Tags || [])
        .map((tag) => {
          if (typeof tag === 'string') {
            return tagLookup.get(tag) || tag;
          }
          return tag?.name || '';
        })
        .filter((name) => !!name)
        .join(', ');

      preparedNotes.push({
        note,
        noteTitle: String(note.Title || '').trim() || 'Untitled note',
        categoryNames,
        tagNames,
        descriptionHtml,
        commentsHtml,
      });
    }

    const docxHtml = this.buildBulkDocxHtmlDocument(preparedNotes, options);
    const rawDocx = await asBlob(docxHtml, {
      orientation: 'portrait',
      margins: {
        top: 720,
        right: 720,
        bottom: 720,
        left: 720,
      },
    });
    const blob = this.toBrowserBlob(rawDocx);
    const filename = this.sanitizeFilenameForExtension(options?.fileName || 'notes_export', '.docx');
    this.downloadBlob(blob, filename);
  }

  private async buildDocDefinition(
    note: Note,
    categoryLookup: Map<string, string>,
    tagLookup: Map<string, string>,
    includeAttachments: boolean = false,
  ): Promise<TDocumentDefinitions> {
    const content: Content[] = await this.buildNoteContent(note, categoryLookup, { tagLookup });
    if (includeAttachments) {
      const attachmentImages = await this.buildAttachmentImages(note);
      content.push(...attachmentImages);
    }
    return {
      content,
      styles: this.getDocStyles(),
    };
  }

  private async buildAttachmentImages(note: Note): Promise<Content[]> {
    if (!note?.Attachments?.length) {
      return [];
    }

    let attachments: Attachment[];
    try {
      attachments = await firstValueFrom(this.fileUpload.getFiles(note._id));
    } catch {
      return [];
    }

    const imageAttachments = (attachments ?? []).filter((att) => isAttachmentImage(att));

    if (!imageAttachments.length) {
      return [];
    }

    const content: Content[] = [
      { text: '', pageBreak: 'before' },
      { text: 'Attachments', style: 'subheader', margin: [0, 0, 0, 8] } as Content,
    ];

    for (const att of imageAttachments) {
      try {
        const blob = await firstValueFrom(this.fileUpload.getFileForNote(note._id, att._id, att.filename));
        const dataUrl = await this.blobToDataUrl(blob);
        const maxWidth = 515;
        const maxHeight = 700;
        const naturalWidth = Number(att.imageWidth ?? 0);
        const naturalHeight = Number(att.imageHeight ?? 0);
        const hasNaturalSize =
          Number.isFinite(naturalWidth) && naturalWidth > 0 && Number.isFinite(naturalHeight) && naturalHeight > 0;
        content.push({
          text: att.filename,
          fontSize: 9,
          color: '#6b7280',
          margin: [0, 4, 0, 2],
        } as Content);
        if (hasNaturalSize && naturalWidth <= maxWidth && naturalHeight <= maxHeight) {
          content.push({
            image: dataUrl,
            width: naturalWidth,
            height: naturalHeight,
            margin: [0, 0, 0, 12],
          } as unknown as Content);
        } else {
          content.push({
            image: dataUrl,
            // Constrain to usable A4 content area.
            fit: [maxWidth, maxHeight],
            margin: [0, 0, 0, 12],
          } as unknown as Content);
        }
      } catch (err) {
        console.warn(`[PDF Export] Could not load image attachment "${att.filename}":`, err);
      }
    }

    return content;
  }

  private async buildNoteContent(
    note: Note,
    categoryLookup: Map<string, string>,
    noteOptions?: {
      includeInTableOfContents?: boolean;
      tagLookup?: Map<string, string>;
    },
  ): Promise<Content[]> {
    const tagsText = this.getTagNames(note, noteOptions?.tagLookup ?? new Map<string, string>());
    const categoriesText = this.getCategoryNames(note, categoryLookup);
    const createdDateText = this.formatPdfDate(note.created_on);
    const referenceDateText = this.formatPdfDate(note.ReferenceDate);

    const content: Content[] = [
      {
        text: this.builder.make(note.Title ?? ''),
        style: 'header',
        alignment: 'center',
        id: this.getPdfDestinationId(note),
        tocItem: noteOptions?.includeInTableOfContents === true,
      },
      '\n',
      {
        columns: [
          { width: 90, text: 'ID:', style: 'subheader' },
          { width: '*', text: this.builder.make(note._id ?? '') },
        ],
      },
      {
        columns: [
          { width: 90, text: 'Type:', style: 'subheader' },
          { width: '*', text: this.builder.make(note.NoteType ?? '') },
          { width: 90, text: 'Modified:', style: 'subheader' },
          { width: '*', text: this.builder.make(note.last_modified ?? '') },
        ],
      },
      {
        columns: [
          { width: 90, text: 'Status:', style: 'subheader' },
          { width: '*', text: this.builder.make(note.Status ?? '') },
          { width: 90, text: 'Created:', style: 'subheader' },
          { width: '*', text: this.builder.make(createdDateText) },
        ],
      },
      {
        columns: [
          { width: 90, text: 'Reference:', style: 'subheader' },
          { width: '*', text: this.builder.make(referenceDateText) },
        ],
      },
      {
        columns: [
          { width: 90, text: 'Tags:', style: 'subheader' },
          { width: '*', text: this.builder.make(tagsText) },
        ],
      },
      {
        columns: [
          { width: 90, text: 'Categories:', style: 'subheader' },
          { width: '*', text: this.builder.make(categoriesText) },
        ],
      },
      {
        text: '\nDescription',
        style: 'subheader',
      },
    ];

    const descrContentAsPdfMake = await this.convertHtmlToPdfContentWithStructuredCollapsibles(note.DescriptionHtml ?? '', {
      tableFontSize: 8,
      lineHeight: 1.0,
      textMargin: [0, 2, 0, 2],
    }, note._id);

    const commContentAsPdfMake = await this.convertHtmlToPdfContentWithStructuredCollapsibles(note.CommentsHtml ?? '', undefined, note._id);

    content.push(...descrContentAsPdfMake);
    content.push('\n\n');
    content.push({
      columns: [
        { width: 90, text: 'Keywords:', style: 'subheader' },
        { width: '*', text: this.builder.make(note.Keywords ?? '') },
      ],
    });
    content.push({
      text: '\nComments',
      style: 'subheader',
    });
    content.push(...commContentAsPdfMake);

    return content;
  }

  private buildTableOfContentsContent(): Content[] {
    return [
      {
        text: 'Table of Contents',
        style: 'tocTitle',
        margin: [0, 0, 0, 12],
      },
      {
        toc: {
          title: {
            text: 'Included notes',
            style: 'tocHeading',
          },
          numberStyle: 'tocPageNumber',
          textStyle: 'tocEntry',
          textMargin: [0, 2, 12, 2],
          numberMargin: [12, 2, 0, 2],
        },
      } as unknown as Content,
      { text: '', pageBreak: 'after' },
    ];
  }

  private getPdfDestinationId(note: Note): string {
    return `note-destination-${String(note._id ?? 'unknown').replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  }

  private async loadCategoryLookup(): Promise<Map<string, string>> {
    try {
      const response = await firstValueFrom(this.dataService.getAllCategories());
      const categories = response.status === 'success' && Array.isArray(response.data) ? response.data : [];
      return new Map(categories.map((category: Category) => [category._id, category.Path || category.Name || category._id]));
    } catch {
      return new Map();
    }
  }

  private async loadTagLookup(): Promise<Map<string, string>> {
    // Prefer the already-loaded in-memory tag list (populated at startup from IndexedDB).
    if (this.dataService.allTags?.length) {
      return new Map(this.dataService.allTags.map((tag: Tag) => [tag._id, tag.name || tag._id]));
    }
    // Fallback: fetch from remote if the in-memory list is empty.
    try {
      const response = await firstValueFrom(this.dataService.getAllTags());
      const tags = response.status === 'success' && Array.isArray(response.data) ? response.data : [];
      return new Map(tags.map((tag: Tag) => [tag._id, tag.name || tag._id]));
    } catch {
      return new Map();
    }
  }

  private getTagNames(note: Note, tagLookup: Map<string, string>): string {
    const noteTags = Array.isArray(note.Tags) ? note.Tags : [];
    if (!noteTags.length) {
      return 'no tags';
    }

    const tagNames = noteTags
      .map((tag) => {
        if (typeof tag === 'string') {
          return tagLookup.get(tag) || tag;
        }

        if (tag && typeof tag === 'object') {
          const tagObj = tag as Tag;
          return tagObj.name || tagLookup.get(tagObj._id) || tagObj._id;
        }

        return '';
      })
      .filter((name) => !!name)
      .sort((left, right) => left.localeCompare(right));

    if (!tagNames.length) {
      return 'no tags';
    }

    return tagNames.join(', ');
  }

  private getCategoryNames(note: Note, categoryLookup: Map<string, string>): string {
    const categoryIds = Array.isArray(note.Categories) ? note.Categories : [];
    if (!categoryIds.length) {
      return 'no categories';
    }

    const categoryNames = categoryIds
      .map((categoryId) => categoryLookup.get(categoryId) || categoryId)
      .sort((left, right) => left.localeCompare(right));

    return categoryNames.join(', ');
  }

  private formatPdfDate(value?: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private getDocStyles(): StyleDictionary {
    return {
      header: { fontSize: 18, bold: true },
      subheader: { fontSize: 14, bold: true },
      tocTitle: { fontSize: 20, bold: true, alignment: 'center' as const },
      tocHeading: { fontSize: 12, bold: true, color: '#374151' },
      tocEntry: { fontSize: 11, color: '#111827' },
      tocPageNumber: { fontSize: 11, bold: true, color: '#2563eb' },
      collapsibleDocumentTitle: { fontSize: 16, bold: true, color: '#111827' },
      collapsibleContainer: { margin: [0, 10, 0, 10] },
      collapsibleTitle: { fontSize: 12, bold: true, color: '#1f2937' },
      collapsibleTitleNested: { fontSize: 11, bold: true, color: '#374151' },
      collapsibleBody: { fontSize: 10, color: '#111827' },
      collapsibleBodyEmpty: { fontSize: 10, italics: true, color: '#6b7280' },
      collapsibleSpacer: { margin: [0, 3, 0, 3] },
    };
  }

  // This function corrects the format of lists in Quill for export / printing.
  // source: https://github.com/slab/quill/issues/3014
  private fixQuillListTags(html: string, listType: string): string {
    if (!html) {
      return '';
    }
    const re = new RegExp(`<ol><li data-list="${listType}">(.*?)<\\/ol>`, 'g');
    let strArr = html.split(re);
    while (strArr.findIndex((ele) => ele === `<ol><li data-list="${listType}">`) !== -1) {
      let indx = strArr.findIndex((ele) => ele === `<ol><li data-list="${listType}">`);
      if (indx) {
        strArr[indx] = `<ul><li data-list="${listType}">`;
        let endTagIndex = strArr.findIndex((ele) => ele === '</ol>');
        strArr[endTagIndex] = '</ul>';
      }
    }
    return strArr.join('');
  }

  private fixQuillTables(html: string): string {
    // Remove <colgroup> and <col> elements
    html = html.replace(/<colgroup>[\s\S]*?<\/colgroup>/g, '');

    // Update <table> tags
    html = html.replace(/<table([^>]*)>/g, (match, attrs) => {
      const styleMatch = attrs.match(/style="([^"]*)"/);
      let newStyle = 'width: 100%;';

      if (styleMatch) {
        const originalStyle = styleMatch[1]
          .split(';')
          .map((s: string) => s.trim())
          .filter((s: string) => s && !s.startsWith('width'))
          .join('; ');

        const mergedStyle = `style="${newStyle} ${originalStyle}"`;
        return `<table ${attrs.replace(styleMatch[0], mergedStyle)}>`;
      }
      return `<table${attrs} style="${newStyle}">`;
    });

    return html;
  }

  private fixPdfMakeTables(content: any): void {
    if (Array.isArray(content)) {
      content.forEach((item) => this.fixPdfMakeTables(item));
    } else if (typeof content === 'object' && content !== null) {
      if (content.table && Array.isArray(content.table.body)) {
        this.normalizePdfMakeTable(content.table);

        if (!content.table.widths) {
          const numCols = content.table.body[0]?.length || 1;
          content.table.widths = Array(numCols).fill('*');
        }
      }

      for (const key in content) {
        if (Object.prototype.hasOwnProperty.call(content, key)) {
          const value = content[key];
          if (typeof value === 'object' && value !== null) {
            this.fixPdfMakeTables(value);
          }
        }
      }
    }
  }

  private normalizePdfMakeTable(table: any): void {
    if (!table || !Array.isArray(table.body)) {
      return;
    }

    if (table.body.length === 0) {
      table.body = [['']];
    }

    // Step 1: normalize each row and expand colSpan placeholders.
    // Use indexed iteration (not Array.map) so sparse-array holes are normalized too.
    const normalizedBody: any[] = [];
    for (let i = 0; i < table.body.length; i++) {
      normalizedBody.push(this.normalizePdfMakeTableRow(table.body[i]));
    }
    table.body = normalizedBody;

    if (!Array.isArray(table.body[0])) {
      table.body.unshift(['']);
    }

    // Step 2: make all rows the same width to avoid pdfmake malformed-row errors.
    const totalCols = Math.max(1, ...table.body.map((row: any[]) => row.length));
    table.body = table.body.map((row: any[]) => {
      const normalizedRow = [...row];
      while (normalizedRow.length < totalCols) {
        normalizedRow.push('');
      }
      return normalizedRow;
    });

    // Step 3: ensure widths align with normalized row width.
    if (Array.isArray(table.widths)) {
      if (table.widths.length < totalCols) {
        table.widths = [...table.widths, ...Array(totalCols - table.widths.length).fill('*')];
      } else if (table.widths.length > totalCols) {
        table.widths = table.widths.slice(0, totalCols);
      }
    }
  }

  private normalizePdfMakeTableRow(row: any): any[] {
    if (!Array.isArray(row)) {
      return [''];
    }

    const normalized: any[] = [];
    for (const originalCell of row) {
      const cell = typeof originalCell === 'undefined' ? '' : originalCell;
      normalized.push(cell);

      if (cell && typeof cell === 'object' && Number.isInteger(cell.colSpan) && cell.colSpan > 1) {
        // pdfmake requires explicit placeholder cells for colSpan coverage.
        for (let i = 1; i < cell.colSpan; i++) {
          normalized.push({});
        }
      }
    }

    return normalized.length ? normalized : [''];
  }

  /**
   * Remove unsupported CSS color formats that pdfmake cannot parse.
   * This handles oklch(), lch(), hwb(), lab() and other modern CSS color formats.
   * Minimal approach: only removes the problematic color values and their semicolons.
   */
  private sanitizeColorsForPdfmake(html: string): string {
    if (!html || typeof html !== 'string') {
      return html;
    }

    // Target only oklch/lch/hwb/lab with their entire function call
    // This handles both color: oklch(...) and background: oklch(...) etc.
    html = html.replace(
      /(?:color|background|background-color|border-color|fill|stroke):\s*(?:oklch|lch|hwb|lab)\([^)]*\)\s*;?/gi,
      '',
    );

    // Only clean up double semicolons that result from the above removal
    html = html.replace(/;\s*;/g, ';');

    return html;
  }

  private async convertHtmlToPdfContentWithStructuredCollapsibles(
    html: string,
    renderOptions?: HtmlToPdfRenderOptions,
    noteId?: string,
  ): Promise<Content[]> {
    const preparedHtml = await this.prepareQuillHtmlContentForPdfExport(html, noteId);
    if (!preparedHtml.trim()) {
      return [];
    }

    const structuredNodes = this.parseExportHtmlToStructuredNodes(preparedHtml);
    return await this.transformStructuredNodesToPdfContent(structuredNodes, 0, renderOptions);
  }

  private parseExportHtmlToStructuredNodes(html: string): StructuredExportNode[] {
    if (!html || typeof html !== 'string' || typeof document === 'undefined') {
      return [];
    }

    const container = document.createElement('div');
    container.innerHTML = html;
    return this.parseStructuredNodesFromContainer(container);
  }

  private parseStructuredNodesFromContainer(container: ParentNode): StructuredExportNode[] {
    const result: StructuredExportNode[] = [];
    let htmlBuffer = '';

    const flushHtmlBuffer = (): void => {
      if (htmlBuffer.trim()) {
        result.push({ type: 'html', html: htmlBuffer });
      }
      htmlBuffer = '';
    };

    for (const child of Array.from(container.childNodes)) {
      if (child instanceof HTMLElement && this.isCollapsibleRootElement(child)) {
        flushHtmlBuffer();
        result.push(this.parseCollapsibleElement(child));
        continue;
      }

      htmlBuffer += this.serializeNodeForHtmlBuffer(child);
    }

    flushHtmlBuffer();
    return result;
  }

  private parseCollapsibleElement(sectionElement: HTMLElement): StructuredCollapsibleNode {
    const titleElement = this.findCollapsibleTitleElement(sectionElement);
    const title = this.normalizeTitleForPdf(titleElement?.textContent || '');
    const bodyElement = this.findCollapsibleBodyElement(sectionElement);

    let bodyContainer: ParentNode;
    if (bodyElement) {
      bodyContainer = bodyElement;
    } else {
      const fallbackContainer = document.createElement('div');
      fallbackContainer.innerHTML = sectionElement.innerHTML;
      const fallbackTitle = this.findFirstMatchingElement(
        fallbackContainer,
        this.collapsibleExportSelectors.titleSelector,
      );
      fallbackTitle?.remove();
      bodyContainer = fallbackContainer;
    }

    const children = this.parseStructuredNodesFromContainer(bodyContainer);
    return {
      type: 'collapsible',
      title,
      children,
      meta: {
        sourceTag: sectionElement.tagName,
      },
    };
  }

  private async transformStructuredNodesToPdfContent(
    nodes: StructuredExportNode[],
    level: number,
    renderOptions?: HtmlToPdfRenderOptions,
  ): Promise<Content[]> {
    const content: Content[] = [];

    for (const node of nodes) {
      if (node.type === 'html') {
        content.push(...this.convertPreparedHtmlFragmentToPdfContent(node.html, renderOptions));
        continue;
      }

      const bodyContent = await this.transformStructuredNodesToPdfContent(node.children, level + 1, renderOptions);
      content.push(this.renderCollapsibleToPdfmake(node.title, bodyContent, level));
    }

    return content;
  }

  private convertPreparedHtmlFragmentToPdfContent(
    preparedHtml: string,
    renderOptions?: HtmlToPdfRenderOptions,
  ): Content[] {
    if (!preparedHtml || !preparedHtml.trim()) {
      return [];
    }

    const htmlToPdfmakeOptions = {
      removeExtraBlanks: true,
    };

    const pdfNode = this.applyEmojiFontToNode(htmlToPdfmake(preparedHtml, htmlToPdfmakeOptions));

    if (typeof renderOptions?.tableFontSize === 'number') {
      this.applyFontSizeToTables(pdfNode, renderOptions.tableFontSize);
    }

    if (typeof renderOptions?.lineHeight === 'number') {
      this.tightenPdfMakeContent(pdfNode, renderOptions.lineHeight, renderOptions.textMargin ?? [0, 2, 0, 2]);
    }

    this.normalizeChecklistPdfLists(pdfNode);
    this.applyChecklistIndentationFromTokens(pdfNode);

    this.fixPdfMakeTables(pdfNode);

    if (Array.isArray(pdfNode)) {
      return pdfNode as Content[];
    }

    return [pdfNode as Content];
  }

  private renderCollapsibleToPdfmake(title: string, bodyContent: Content[], level: number): Content {
    const visualLevel = this.clampCollapsibleVisualLevel(level);
    const indentation = visualLevel * 12;
    const accentColorByLevel = ['#94a3b8', '#cbd5e1', '#dbe3ec', '#e5e7eb'];
    const titleBgByLevel = ['#f3f4f6', '#f8fafc', '#fbfcfe', '#ffffff'];
    const accentColor = accentColorByLevel[Math.min(visualLevel, accentColorByLevel.length - 1)];
    const titleFill = titleBgByLevel[Math.min(visualLevel, titleBgByLevel.length - 1)];
    const titleStyle = visualLevel > 0 ? 'collapsibleTitleNested' : 'collapsibleTitle';
    const blockVerticalSpacing = visualLevel > 0 ? 8 : 12;
    const bodyStack = bodyContent.length
      ? bodyContent
      : ([{ text: this.builder.make('No content in section.'), style: 'collapsibleBodyEmpty' }] as Content[]);

    return {
      style: 'collapsibleContainer',
      margin: [indentation, blockVerticalSpacing, 0, blockVerticalSpacing],
      stack: [
        {
          columns: [
            {
              width: 3,
              text: ' ',
              fillColor: accentColor,
            },
            {
              width: 8,
              text: '',
            },
            {
              width: '*',
              stack: [
                {
                  text: this.builder.make(this.normalizeTitleForPdf(title)),
                  style: titleStyle,
                  fillColor: titleFill,
                  margin: [8, 6, 8, 6],
                },
                {
                  canvas: [
                    {
                      type: 'line',
                      x1: 0,
                      y1: 0,
                      x2: Math.max(120, 500 - indentation),
                      y2: 0,
                      lineWidth: 0.5,
                      lineColor: '#d1d5db',
                    },
                  ],
                  margin: [0, 1, 0, 6],
                },
                {
                  style: 'collapsibleBody',
                  stack: bodyStack,
                  margin: [0, 0, 0, 2],
                },
              ],
            },
          ],
        },
      ],
    } as Content;
  }

  private clampCollapsibleVisualLevel(level: number): number {
    if (!Number.isFinite(level)) {
      return 0;
    }
    return Math.max(0, Math.min(this.maxCollapsibleVisualDepth, Math.floor(level)));
  }

  private isCollapsibleRootElement(element: HTMLElement): boolean {
    return this.matchesAnySelector(element, this.collapsibleExportSelectors.rootSelector);
  }

  private findCollapsibleTitleElement(sectionElement: HTMLElement): HTMLElement | null {
    return this.findDirectMatchingElement(sectionElement, this.collapsibleExportSelectors.titleSelector)
      || this.findFirstMatchingElement(sectionElement, this.collapsibleExportSelectors.titleSelector);
  }

  private findCollapsibleBodyElement(sectionElement: HTMLElement): HTMLElement | null {
    return this.findDirectMatchingElement(sectionElement, this.collapsibleExportSelectors.bodySelector)
      || this.findFirstMatchingElement(sectionElement, this.collapsibleExportSelectors.bodySelector);
  }

  private findDirectMatchingElement(root: HTMLElement, selectorList: string): HTMLElement | null {
    const selectors = selectorList
      .split(',')
      .map((selector) => selector.trim())
      .filter((selector) => selector.length > 0);

    for (const child of Array.from(root.children)) {
      for (const selector of selectors) {
        if (child.matches(selector)) {
          return child as HTMLElement;
        }
      }
    }

    return null;
  }

  private findFirstMatchingElement(root: ParentNode, selectorList: string): HTMLElement | null {
    const selectors = selectorList
      .split(',')
      .map((selector) => selector.trim())
      .filter((selector) => selector.length > 0);

    for (const selector of selectors) {
      const found = root.querySelector(selector);
      if (found instanceof HTMLElement) {
        return found;
      }
    }

    return null;
  }

  private matchesAnySelector(element: HTMLElement, selectorList: string): boolean {
    return selectorList
      .split(',')
      .map((selector) => selector.trim())
      .filter((selector) => selector.length > 0)
      .some((selector) => element.matches(selector));
  }

  private serializeNodeForHtmlBuffer(node: ChildNode): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return this.escapeHtmlText(node.textContent || '');
    }

    if (node instanceof HTMLElement) {
      return node.outerHTML;
    }

    return '';
  }

  private escapeHtmlText(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // This function prepares the HTML content for PDF export. This improves lists, tables-widths and line-breaks.
  private async prepareQuillHtmlContentForPdfExport(html: string, noteId?: string): Promise<string> {
    if (!html || typeof html !== 'string') {
      return '';
    }
    let fixedHtml = this.fixQuillListTags(html, 'bullet');
    fixedHtml = this.fixQuillListTags(fixedHtml, 'checked');
    fixedHtml = this.fixQuillListTags(fixedHtml, 'unchecked');

    // replace non-breaking spaces with normal spaces
    fixedHtml = fixedHtml.replace(/\u00A0/g, ' ');
    fixedHtml = fixedHtml.replace(/&nbsp;/g, ' ');

    // Convert tiptap task list items to plain list items with checkbox characters
    fixedHtml = this.fixTiptapTaskListsForPdf(fixedHtml);
    fixedHtml = this.fixQuillChecklistItemsForPdf(fixedHtml);

    // Sanitize unsupported CSS colors before processing
    fixedHtml = this.sanitizeColorsForPdfmake(fixedHtml);
    fixedHtml = await this.embedImagesAsDataUrls(fixedHtml, noteId);

    fixedHtml = this.fixQuillTables(fixedHtml);
    return fixedHtml;
  }

  /**
   * Converts tiptap task-list items into plain <li> elements with a Unicode
   * checkbox character (☐ / ☑) so that html-to-pdfmake renders them correctly.
   *
   * Tiptap serialises each task item as:
   *   <li data-type="taskItem" data-checked="true|false">
   *     <label><input type="checkbox" [checked]></label>
   *     <div><p>text…</p></div>
   *   </li>
   *
   * html-to-pdfmake cannot render <input> elements and treats the <div> as a
   * block, producing a blank line before the text. This method strips the
   * label/input, unwraps the content <div>, and prepends the checkbox glyph
   * directly to the first paragraph so it appears inline with the text.
   */
  private fixTiptapTaskListsForPdf(html: string): string {
    if (!html || typeof html !== 'string' || typeof document === 'undefined') {
      return html;
    }

    const container = document.createElement('div');
    container.innerHTML = html;

    const isTaskListElement = (el: Element | null): el is HTMLElement => {
      return (
        !!el &&
        el instanceof HTMLElement &&
        (el.matches('ul[data-type="taskList"]') || el.matches('ul.task-list') || el.matches('ul.contains-task-list'))
      );
    };

    // Step 1: prepare each task item — remove label/input, unwrap content div, prepend glyph.
    const taskItems = Array.from(container.querySelectorAll('li')).filter(
      (item) =>
        item.matches('li[data-type="taskItem"], li.task-item, li.task-list-item') ||
        !!item.querySelector('input[type="checkbox"]'),
    );

    const hasTaskLists = container.querySelector('ul[data-type="taskList"], ul.task-list, ul.contains-task-list') !== null;
    if (!taskItems.length && !hasTaskLists) {
      return html;
    }
    for (const item of taskItems) {
      if (!(item instanceof HTMLElement)) {
        continue;
      }

      const checkedAttr = (item.getAttribute('data-checked') || '').toLowerCase();
      const isChecked = checkedAttr === 'true' || item.classList.contains('is-checked') || item.classList.contains('checked');
      // \uFE0F (emoji variation selector-16) forces ☐ through NotoEmoji font so both
      // glyphs render at the same width/size as ☑ which is already emoji-routed.
      const checkChar = isChecked ? '\u2611' : '\u2610\uFE0F'; // ☑ or ☐️

      // Remove the <label><input type="checkbox"></label>
      item.querySelector('label')?.remove();

      // Unwrap the content <div> — move its children directly into the <li>
      const contentDiv = Array.from(item.children).find(
        (child): child is HTMLDivElement => child instanceof HTMLDivElement,
      );
      if (contentDiv) {
        while (contentDiv.firstChild) {
          item.insertBefore(contentDiv.firstChild, contentDiv);
        }
        contentDiv.remove();
      }

      // Prepend the checkbox glyph to the first <p> so it sits inline with the text.
      // Use an explicit font-size span so both ☐ and ☑ render at the same declared size
      // regardless of which font (Roboto vs NotoEmoji) each glyph is routed to.
      const glyphSpan = document.createElement('span');
      glyphSpan.style.fontSize = '13px';
      glyphSpan.textContent = `${checkChar}\u00A0`; // non-breaking space keeps glyph and text together
      const firstPara =
        Array.from(item.children).find((child): child is HTMLParagraphElement => child instanceof HTMLParagraphElement) ??
        item.querySelector('p');
      if (firstPara) {
        firstPara.insertBefore(glyphSpan, firstPara.firstChild);
      } else {
        item.insertBefore(glyphSpan, item.firstChild);
      }
    }

    // Step 2: Keep native nested list structure (same model as bullet/numbered lists)
    // and suppress list markers later in pdfmake while preserving nesting indentation.
    const taskLists = Array.from(
      container.querySelectorAll('ul[data-type="taskList"], ul.task-list, ul.contains-task-list'),
    ).filter((list): list is HTMLElement => list instanceof HTMLElement);

    for (const list of taskLists) {
      list.setAttribute('data-pdf-checklist', 'true');
      list.style.listStyleType = 'none';
      list.style.paddingLeft = `${this.checklistIndentEmPerLevel}em`;
    }

    return container.innerHTML;
  }

  private normalizeChecklistPdfLists(node: any): void {
    if (!node) {
      return;
    }

    if (Array.isArray(node)) {
      for (const child of node) {
        this.normalizeChecklistPdfLists(child);
      }
      return;
    }

    if (typeof node !== 'object') {
      return;
    }

    if (Array.isArray(node.ul) && this.isChecklistPdfList(node.ul)) {
      node.type = 'none';
    }

    for (const key of ['stack', 'columns', 'ul', 'ol', 'content']) {
      if (node[key]) {
        this.normalizeChecklistPdfLists(node[key]);
      }
    }
  }

  private isChecklistPdfList(items: any[]): boolean {
    if (!Array.isArray(items) || items.length === 0) {
      return false;
    }

    let checklistItemCount = 0;
    for (const item of items) {
      const leadingText = this.extractLeadingText(item);
      if (this.checklistMarkerRegex.test(leadingText)) {
        checklistItemCount++;
      }
    }

    return checklistItemCount > 0 && checklistItemCount === items.length;
  }

  private extractLeadingText(node: any): string {
    if (node == null) {
      return '';
    }
    if (typeof node === 'string') {
      return node;
    }
    if (typeof node !== 'object') {
      return '';
    }

    if (typeof node.text === 'string') {
      return node.text;
    }

    if (Array.isArray(node.text)) {
      for (const part of node.text) {
        const value = this.extractLeadingText(part);
        if (value) {
          return value;
        }
      }
    }

    for (const key of ['stack', 'columns', 'content']) {
      if (Array.isArray(node[key])) {
        for (const child of node[key]) {
          const value = this.extractLeadingText(child);
          if (value) {
            return value;
          }
        }
      }
    }

    return '';
  }

  private fixQuillChecklistItemsForPdf(html: string): string {
    if (!html || typeof html !== 'string' || typeof document === 'undefined') {
      return html;
    }
    if (!html.includes('data-list="checked"') && !html.includes('data-list="unchecked"')) {
      return html;
    }

    const container = document.createElement('div');
    container.innerHTML = html;

    const checklistItems = Array.from(
      container.querySelectorAll('li[data-list="checked"], li[data-list="unchecked"]'),
    ).filter((item): item is HTMLElement => item instanceof HTMLElement);

    for (const item of checklistItems) {
      const isChecked = item.getAttribute('data-list') === 'checked';
      const checkChar = isChecked ? '\u2611' : '\u2610\uFE0F';
      const depth = this.extractQuillIndentDepth(item);

      const paragraph = document.createElement('p');
      paragraph.innerHTML = item.innerHTML;

      const glyphSpan = document.createElement('span');
      glyphSpan.style.fontSize = '13px';
      glyphSpan.textContent = `${this.buildChecklistDepthToken(depth)}${checkChar}\u00A0`;
      paragraph.insertBefore(glyphSpan, paragraph.firstChild);

      item.parentNode?.insertBefore(paragraph, item);
      item.remove();
    }

    const emptyLists = Array.from(container.querySelectorAll('ul, ol')).filter(
      (list): list is HTMLElement => list instanceof HTMLElement && list.querySelector('li') === null,
    );
    for (const list of emptyLists) {
      list.remove();
    }

    return container.innerHTML;
  }

  private extractQuillIndentDepth(item: HTMLElement): number {
    for (const className of Array.from(item.classList)) {
      const match = className.match(/^ql-indent-(\d+)$/);
      if (match) {
        const parsed = Number(match[1]);
        if (Number.isFinite(parsed) && parsed >= 0) {
          return parsed;
        }
      }
    }
    return 0;
  }

  private buildChecklistDepthToken(depth: number): string {
    const safeDepth = Math.max(0, Math.floor(Number(depth) || 0));
    return `${this.checklistDepthTokenPrefix}${safeDepth}]]`;
  }

  private applyChecklistIndentationFromTokens(node: any): void {
    if (!node) {
      return;
    }

    if (Array.isArray(node)) {
      for (const child of node) {
        this.applyChecklistIndentationFromTokens(child);
      }
      return;
    }

    if (typeof node === 'string') {
      return;
    }

    if (typeof node !== 'object') {
      return;
    }

    let detectedDepth = 0;

    if (typeof node.text === 'string') {
      const { cleaned, maxDepth } = this.stripChecklistDepthTokens(node.text);
      node.text = cleaned;
      detectedDepth = Math.max(detectedDepth, maxDepth);
    } else if (Array.isArray(node.text)) {
      for (let i = 0; i < node.text.length; i++) {
        const fragment = node.text[i];
        if (typeof fragment === 'string') {
          const { cleaned, maxDepth } = this.stripChecklistDepthTokens(fragment);
          node.text[i] = cleaned;
          detectedDepth = Math.max(detectedDepth, maxDepth);
        } else if (fragment && typeof fragment === 'object') {
          if (typeof fragment.text === 'string') {
            const { cleaned, maxDepth } = this.stripChecklistDepthTokens(fragment.text);
            fragment.text = cleaned;
            detectedDepth = Math.max(detectedDepth, maxDepth);
          }
          this.applyChecklistIndentationFromTokens(fragment);
        }
      }
    }

    if (detectedDepth > 0) {
      const existing = Array.isArray(node.margin) ? node.margin : [0, 2, 0, 2];
      const left = Number(existing[0] ?? 0);
      const top = Number(existing[1] ?? 2);
      const right = Number(existing[2] ?? 0);
      const bottom = Number(existing[3] ?? 2);
      node.margin = [
        left + detectedDepth * this.checklistIndentPdfPointsPerLevel,
        top,
        right,
        bottom,
      ];
    }

    for (const key of ['stack', 'columns', 'ul', 'ol', 'content']) {
      if (node[key]) {
        this.applyChecklistIndentationFromTokens(node[key]);
      }
    }
  }

  private stripChecklistDepthTokens(text: string): { cleaned: string; maxDepth: number } {
    if (!text || typeof text !== 'string') {
      return { cleaned: text, maxDepth: 0 };
    }

    const tokenRegex = /\[\[TASK_DEPTH_(\d+)\]\]/g;
    let maxDepth = 0;
    const cleaned = text.replace(tokenRegex, (_, depthText: string) => {
      const depth = Number(depthText);
      if (Number.isFinite(depth)) {
        maxDepth = Math.max(maxDepth, Math.max(0, Math.floor(depth)));
      }
      return '';
    });

    return { cleaned, maxDepth };
  }

  private async embedImagesAsDataUrls(html: string, noteId?: string): Promise<string> {
    if (!html || !/<img\b/i.test(html) || typeof document === 'undefined') {
      return html;
    }

    const PDF_PAGE_WIDTH = 515;  // usable A4 width in pdfmake points (595 - 2×40 margins)
    const PDF_PAGE_HEIGHT = 750; // usable A4 height in pdfmake points

    const container = document.createElement('div');
    container.innerHTML = html;
    const images = Array.from(container.querySelectorAll('img'));

    for (const img of images) {
      const rawSrc = (img.getAttribute('src') || '').trim();
      const imageSourceType = img.getAttribute('data-image-source-type');
      const attachmentId = (img.getAttribute('data-attachment-id') || '').trim();

      try {
        let dataUrl: string;

        if (imageSourceType === 'attachment' && attachmentId) {
          // Attachment images need the authenticated FileUploadService (plain fetch would 401).
          const blob = await firstValueFrom(this.fileUpload.getFileForNote(noteId ?? '', attachmentId));
          dataUrl = await this.blobToPdfMakeSafeDataUrl(blob);
        } else if (rawSrc.startsWith('data:image/')) {
          dataUrl = await this.normalizeDataUrlForPdfMake(rawSrc);
        } else if (!rawSrc || rawSrc.startsWith('blob:')) {
          // Stale blob URL or missing src — nothing we can do without an attachment ID.
          img.remove();
          continue;
        } else {
          const absoluteSrc = this.resolveImageUrl(rawSrc);
          dataUrl = await this.fetchImageAsDataUrl(absoluteSrc);
        }

        img.setAttribute('src', dataUrl);

        const resolvedSize = this.resolvePdfImageSize(img, PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT);
        img.setAttribute('width', String(resolvedSize.width));
        img.setAttribute('height', String(resolvedSize.height));
        this.stripInlineImageSizeStyles(img);
      } catch {
        // Replace unresolved images with a visible placeholder so the PDF user
        // knows an image was present rather than silently losing content.
        const url = (img.getAttribute('src') || img.getAttribute('data-original-url') || '').trim();
        const placeholder = document.createElement('span');
        placeholder.textContent = url ? `[Image: ${url}]` : '[Image not available]';
        img.replaceWith(placeholder);
      }
    }

    return container.innerHTML;
  }

  private resolvePdfImageSize(
    img: HTMLImageElement,
    pageWidthPt: number,
    pageHeightPt: number,
  ): { width: number; height: number } {
    const style = img.getAttribute('style') || '';
    const styleWidthPx = this.extractCssPx(style, 'width');
    const styleHeightPx = this.extractCssPx(style, 'height');
    const widthAttrPx = this.parseDimensionToPx(img.getAttribute('width'));
    const heightAttrPx = this.parseDimensionToPx(img.getAttribute('height'));
    const naturalWidthPx = this.parseDimensionToPx(img.getAttribute('data-natural-width'));
    const naturalHeightPx = this.parseDimensionToPx(img.getAttribute('data-natural-height'));

    let preferredWidthPt = this.toPdfPoints(styleWidthPx ?? widthAttrPx ?? null);
    let preferredHeightPt = this.toPdfPoints(styleHeightPx ?? heightAttrPx ?? null);
    const naturalWidthPt = this.toPdfPoints(naturalWidthPx ?? null);
    const naturalHeightPt = this.toPdfPoints(naturalHeightPx ?? null);

    if (preferredWidthPt && !preferredHeightPt && naturalWidthPt && naturalHeightPt) {
      preferredHeightPt = preferredWidthPt * (naturalHeightPt / naturalWidthPt);
    } else if (!preferredWidthPt && preferredHeightPt && naturalWidthPt && naturalHeightPt) {
      preferredWidthPt = preferredHeightPt * (naturalWidthPt / naturalHeightPt);
    }

    let finalWidthPt: number;
    let finalHeightPt: number;

    if (preferredWidthPt && preferredHeightPt) {
      finalWidthPt = preferredWidthPt;
      finalHeightPt = preferredHeightPt;
    } else if (naturalWidthPt && naturalHeightPt) {
      finalWidthPt = naturalWidthPt;
      finalHeightPt = naturalHeightPt;
    } else {
      finalWidthPt = pageWidthPt;
      finalHeightPt = Math.round(pageWidthPt * 0.6);
    }

    const scale = Math.min(1, pageWidthPt / finalWidthPt, pageHeightPt / finalHeightPt);
    finalWidthPt = Math.max(1, Math.round(finalWidthPt * scale));
    finalHeightPt = Math.max(1, Math.round(finalHeightPt * scale));

    return { width: finalWidthPt, height: finalHeightPt };
  }

  private extractCssPx(style: string, property: 'width' | 'height'): number | null {
    const match = style.match(new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([\\d.]+)(?:\\s*px)?`, 'i'));
    if (!match) {
      return null;
    }
    return this.parseDimensionToPx(match[1]);
  }

  private parseDimensionToPx(value: string | null | undefined): number | null {
    if (!value) {
      return null;
    }

    const match = String(value).trim().match(/^([\d.]+)(?:\s*px)?$/i);
    if (!match) {
      return null;
    }

    const parsed = Number.parseFloat(match[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private toPdfPoints(valuePx: number | null): number | null {
    if (!valuePx || !Number.isFinite(valuePx) || valuePx <= 0) {
      return null;
    }
    return valuePx * this.cssPxToPdfPt;
  }

  private stripInlineImageSizeStyles(img: HTMLImageElement): void {
    const style = img.getAttribute('style');
    if (!style) {
      return;
    }

    const styleParts = style
      .split(';')
      .map((part) => part.trim())
      .filter((part) => !!part)
      .filter((part) => !/^width\s*:/i.test(part) && !/^height\s*:/i.test(part));

    if (styleParts.length) {
      img.setAttribute('style', styleParts.join('; ') + ';');
    } else {
      img.removeAttribute('style');
    }
  }

  private resolveImageUrl(src: string): string {
    if (typeof window === 'undefined') {
      return src;
    }

    if (/^(https?:|data:|blob:)/i.test(src)) {
      return src;
    }

    return new URL(src, window.location.origin).toString();
  }

  private async fetchImageAsDataUrl(src: string): Promise<string> {
    // First try: explicit CORS fetch (works for servers that advertise CORS headers).
    try {
      const response = await fetch(src, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      return await this.blobToPdfMakeSafeDataUrl(blob);
    } catch {
      // Second try: canvas approach — succeeds for CORS-enabled images and
      // same-origin images even when the first fetch variant fails.
      return await this.fetchImageViaCanvas(src);
    }
  }

  private fetchImageViaCanvas(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (e) {
          // Canvas tainted (cross-origin without CORS headers) — nothing we can do.
          reject(e);
        }
      };
      img.onerror = () => reject(new Error(`Image load failed: ${src}`));
      img.src = src;
    });
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }
        reject(new Error('Failed to convert image blob to data URL'));
      };
      reader.onerror = () => reject(reader.error || new Error('Failed to read image blob'));
      reader.readAsDataURL(blob);
    });
  }

  private async blobToPdfMakeSafeDataUrl(blob: Blob): Promise<string> {
    const mime = (blob.type || '').toLowerCase();

    if (this.pdfmakeSupportedImageMimeTypes.has(mime)) {
      return this.blobToDataUrl(blob);
    }

    return this.blobToPngDataUrl(blob);
  }

  private async normalizeDataUrlForPdfMake(dataUrl: string): Promise<string> {
    const trimmed = (dataUrl || '').trim();
    const mime = this.extractDataUrlMimeType(trimmed);

    if (!mime) {
      throw new Error('Invalid image data URL');
    }

    const isSupported = this.pdfmakeSupportedImageMimeTypes.has(mime);
    const isBase64 = /^data:image\/[^;]+;base64,/i.test(trimmed);

    if (isSupported && isBase64) {
      return trimmed;
    }

    return this.imageSourceToPngDataUrl(trimmed);
  }

  private extractDataUrlMimeType(dataUrl: string): string | null {
    const match = /^data:(image\/[^;,]+)[;,]/i.exec(dataUrl);
    return match ? match[1].toLowerCase() : null;
  }

  private async blobToPngDataUrl(blob: Blob): Promise<string> {
    const objectUrl = URL.createObjectURL(blob);
    try {
      return await this.imageSourceToPngDataUrl(objectUrl);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private imageSourceToPngDataUrl(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context for image conversion'));
            return;
          }
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Image conversion to PNG failed'));
      img.src = src;
    });
  }

  private sanitizeFilename(title: string): string {
    return this.sanitizeFilenameForExtension(title, '.pdf');
  }

  private buildDocxHtmlDocument(
    note: Note,
    content: {
      noteTitle: string;
      categoryNames: string;
      tagNames: string;
      descriptionHtml: string;
      commentsHtml: string;
    },
  ): string {
    const metaRows = [
      ['Status', note.Status],
      ['Project', note.ProjectName],
      ['Category', content.categoryNames],
      ['Tags', content.tagNames],
      ['Keywords', note.Keywords],
      ['AI Keywords', note.AiKeywords],
      ['Created', this.formatPdfDate(note.created_on)],
      ['Reference Date', this.formatPdfDate(note.ReferenceDate)],
    ]
      .filter(([, value]) => String(value ?? '').trim().length > 0)
      .map(
        ([label, value]) => `
          <tr>
            <th>${this.escapeHtmlText(String(label))}</th>
            <td>${this.escapeHtmlText(String(value))}</td>
          </tr>`,
      )
      .join('');

    const renderSection = (title: string, html: string): string => {
      if (!String(html || '').trim()) {
        return '';
      }
      return `
        <section class="docx-section">
          <h2>${this.escapeHtmlText(title)}</h2>
          <div class="rich-content">${html}</div>
        </section>`;
    };

    return `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${this.escapeHtmlText(content.noteTitle)}</title>
          <style>
            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 12pt;
              line-height: 1.35;
              color: #111827;
            }
            h1, h2, h3, h4, h5, h6 {
              font-family: Arial, Helvetica, sans-serif;
              margin: 0 0 10pt;
              color: #111827;
            }
            h1 { font-size: 22pt; }
            h2 { font-size: 16pt; margin-top: 18pt; }
            h3 { font-size: 14pt; }
            p { margin: 0 0 8pt; }
            ul, ol { margin: 0 0 10pt 20pt; }
            li { margin: 0 0 4pt; }
            blockquote {
              border-left: 3px solid #cbd5e1;
              margin: 10pt 0;
              padding-left: 10pt;
              color: #475569;
            }
            pre, code {
              font-family: 'Courier New', Courier, monospace;
            }
            pre {
              background: #f8fafc;
              border: 1px solid #cbd5e1;
              padding: 8pt;
              white-space: pre-wrap;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 10pt 0;
            }
            th, td {
              border: 1px solid #64748b;
              padding: 6pt 8pt;
              vertical-align: top;
            }
            th {
              background: #e2e8f0;
              text-align: left;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            .docx-meta {
              margin: 0 0 18pt;
            }
            .docx-meta th {
              width: 120pt;
            }
            .docx-section {
              margin-top: 14pt;
            }
            .collapsible-section,
            [data-type="collapsible"] {
              border: 1px solid #cbd5e1;
              padding: 8pt;
              margin: 10pt 0;
            }
            .collapsible-title,
            [data-type="collapsible-title"] {
              font-weight: 700;
              margin-bottom: 6pt;
            }
            .tiptap-image-node,
            .tableWrapper {
              display: block;
              width: 100%;
            }
          </style>
        </head>
        <body>
          <h1>${this.escapeHtmlText(content.noteTitle)}</h1>
          ${metaRows ? `<table class="docx-meta"><tbody>${metaRows}</tbody></table>` : ''}
          ${renderSection('Description', content.descriptionHtml)}
          ${renderSection('Comments', content.commentsHtml)}
        </body>
      </html>`;
  }

  private buildBulkDocxHtmlDocument(notes: DocxPreparedNoteContent[], options?: BulkDocxExportOptions): string {
    const includeTableOfContents = options?.includeTableOfContents === true;
    const pageBreakBetweenNotes = options?.pageBreakBetweenNotes === true;
    const pageNumbers = options?.pageNumbers === true;
    const includeMetaData = options?.includeMetaData === true;
    const firstNotePageNumber = includeTableOfContents ? 2 : 1;

    const tocRows = includeTableOfContents
      ? notes
          .map((entry, index) => {
            const pageNumber = firstNotePageNumber + index;
            return `
              <tr>
                <td class="docx-toc-title">${this.escapeHtmlText(entry.noteTitle)}</td>
                ${pageNumbers ? `<td class="docx-toc-page">${pageNumber}</td>` : ''}
              </tr>`;
          })
          .join('')
      : '';

    const tocSection = includeTableOfContents
      ? `
        <section class="docx-toc-section">
          <h1>Table of Contents</h1>
          <table class="docx-toc-table">
            <tbody>
              ${tocRows}
            </tbody>
          </table>
        </section>`
      : '';

    const notesHtml = notes
      .map((entry, index) => this.renderBulkDocxNoteSection(entry, index, { pageBreakBetweenNotes, includeMetaData }))
      .join('');

    return `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${this.escapeHtmlText(notes[0]?.noteTitle || 'Notes export')}</title>
          <style>
            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 12pt;
              line-height: 1.35;
              color: #111827;
            }
            h1, h2, h3, h4, h5, h6 {
              font-family: Arial, Helvetica, sans-serif;
              margin: 0 0 10pt;
              color: #111827;
            }
            h1 { font-size: 22pt; }
            h2 { font-size: 16pt; margin-top: 18pt; }
            h3 { font-size: 14pt; }
            p { margin: 0 0 8pt; }
            ul, ol { margin: 0 0 10pt 20pt; }
            li { margin: 0 0 4pt; }
            blockquote {
              border-left: 3px solid #cbd5e1;
              margin: 10pt 0;
              padding-left: 10pt;
              color: #475569;
            }
            pre, code {
              font-family: 'Courier New', Courier, monospace;
            }
            pre {
              background: #f8fafc;
              border: 1px solid #cbd5e1;
              padding: 8pt;
              white-space: pre-wrap;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 10pt 0;
            }
            th, td {
              border: 1px solid #64748b;
              padding: 6pt 8pt;
              vertical-align: top;
            }
            th {
              background: #e2e8f0;
              text-align: left;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            .docx-section {
              margin-top: 14pt;
            }
            .docx-meta {
              margin: 0 0 10pt;
            }
            .docx-meta th {
              width: 100pt;
            }
            .docx-meta td, .docx-meta th {
              padding: 2pt 4pt;
              font-size: 9pt;
              line-height: 1.2;
            }
            .docx-toc-section {
              page-break-after: always;
            }
            .docx-toc-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 14pt;
            }
            .docx-toc-table td {
              border: none;
              padding: 2pt 0;
            }
            .docx-toc-title {
              width: auto;
            }
            .docx-toc-page {
              width: 42pt;
              text-align: right;
              white-space: nowrap;
            }
            .docx-note-section {
              margin-top: 0;
            }
            .docx-note-page-break {
              page-break-before: always;
            }
            .docx-note-page-number {
              text-align: right;
              font-size: 10pt;
              color: #6b7280;
              margin: 0 0 8pt;
            }
            .collapsible-section,
            [data-type="collapsible"] {
              border: 1px solid #cbd5e1;
              padding: 8pt;
              margin: 10pt 0;
            }
            .collapsible-title,
            [data-type="collapsible-title"] {
              font-weight: 700;
              margin-bottom: 6pt;
            }
            .tiptap-image-node,
            .tableWrapper {
              display: block;
              width: 100%;
            }
          </style>
        </head>
        <body>
          ${pageNumbers ? `<div style="mso-element:footer" id="f1"><p style="text-align:center;font-size:9pt;color:#6b7280;margin:0"><span style='mso-field-code:" PAGE \\* MERGEFORMAT "'>1</span></p></div>` : ''}
          ${tocSection}
          ${notesHtml}
        </body>
      </html>`;
  }

  private renderBulkDocxNoteSection(
    entry: DocxPreparedNoteContent,
    index: number,
    options: { pageBreakBetweenNotes: boolean; includeMetaData: boolean },
  ): string {
    const metaRows = options.includeMetaData ? this.renderDocxMetaRows(entry.note, entry.categoryNames, entry.tagNames) : '';
    const notePageBreak = options.pageBreakBetweenNotes && index > 0;
    const pageBreakMarker = notePageBreak
      ? '<br clear="all" style="mso-special-character:line-break;page-break-before:always;" />'
      : '';

    return `
      ${pageBreakMarker}
      <section class="docx-section docx-note-section ${notePageBreak ? 'docx-note-page-break' : ''}">
        <h1>${this.escapeHtmlText(entry.noteTitle)}</h1>
        ${metaRows ? `<table class="docx-meta"><tbody>${metaRows}</tbody></table>` : ''}
        ${this.renderDocxSection('Description', entry.descriptionHtml)}
        ${this.renderDocxSection('Comments', entry.commentsHtml)}
      </section>`;
  }

  private renderDocxSection(title: string, html: string): string {
    if (!String(html || '').trim()) {
      return '';
    }
    return `
      <section class="docx-section">
        <h2>${this.escapeHtmlText(title)}</h2>
        <div class="rich-content">${html}</div>
      </section>`;
  }

  private renderDocxMetaRows(note: Note, categoryNames: string, tagNames: string): string {
    return [
      ['Status', note.Status],
      ['Project', note.ProjectName],
      ['Category', categoryNames],
      ['Tags', tagNames],
      ['Keywords', note.Keywords],
      ['AI Keywords', note.AiKeywords],
      ['Created', this.formatPdfDate(note.created_on)],
      ['Reference Date', this.formatPdfDate(note.ReferenceDate)],
    ]
      .filter(([, value]) => String(value ?? '').trim().length > 0)
      .map(
        ([label, value]) => `
          <tr>
            <th>${this.escapeHtmlText(String(label))}</th>
            <td>${this.escapeHtmlText(String(value))}</td>
          </tr>`,
      )
      .join('');
  }

  sanitizeFilenameForExtension(title: string, extension: '.pdf' | '.docx' | '.json'): string {
    const invalidCharsRegex = /[<>:"/\\|?*\x00-\x1F]/g;
    const windowsReservedNames = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;

    let sanitized = String(title ?? '');
    sanitized = sanitized.replace(invalidCharsRegex, '');
    sanitized = sanitized.replace(/[\u202e\u202d\u202a\u200e\u200f]/g, '');
    sanitized = sanitized.replace(/\.{2,}/g, '.');
    sanitized = sanitized.replace(/\s+/g, '_');
    sanitized = sanitized.replace(/_+/g, '_');
    sanitized = sanitized.replace(/^\.+|\.+$/g, '');
    sanitized = sanitized.replace(/^_+|_+$/g, '');
    sanitized = sanitized.substring(0, 120);

    if (!sanitized || windowsReservedNames.test(sanitized)) {
      sanitized = 'untitled';
    }

    if (!sanitized.toLowerCase().endsWith(extension)) {
      sanitized = `${sanitized}${extension}`;
    }

    return sanitized;
  }

  private toBrowserBlob(value: Awaited<ReturnType<typeof asBlob>>): Blob {
    if (value instanceof Blob) {
      return value;
    }
    return new Blob([value], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    if (typeof document === 'undefined') {
      return;
    }
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }

  private normalizeTitleForPdf(title: string): string {
    const trimmed = String(title ?? '').trim();
    return trimmed.length > 0 ? trimmed : 'Untitled section';
  }

  private applyFontSizeToTables(node: any, size: number): any {
    if (Array.isArray(node)) {
      return node.map((child) => this.applyFontSizeToTables(child, size));
    } else if (typeof node === 'object' && node !== null) {
      if (node.table) {
        node.fontSize = size;

        if (Array.isArray(node.table.body)) {
          node.table.body = node.table.body.map((row: any[]) =>
            row.map((cell: any) => this.applyFontSizeToTables(cell, size)),
          );
        }
      }

      for (const key of ['stack', 'columns', 'ul', 'ol', 'text', 'content']) {
        if (node[key]) {
          node[key] = this.applyFontSizeToTables(node[key], size);
        }
      }
    }

    return node;
  }

  private tightenPdfMakeContent(node: any, lineHeight = 1.0, margin = [0, 2, 0, 2]): any {
    if (Array.isArray(node)) {
      return node.map((child) => this.tightenPdfMakeContent(child, lineHeight, margin));
    }

    if (typeof node === 'object' && node !== null) {
      if (typeof node.text !== 'undefined') {
        node.lineHeight = lineHeight;
        node.margin = margin;
      }

      if (node.table && Array.isArray(node.table.body)) {
        node.table.body = node.table.body.map((row: any[]) =>
          row.map((cell: any) => this.tightenPdfMakeContent(cell, lineHeight, margin)),
        );
      }

      for (const key of ['stack', 'columns', 'ul', 'ol', 'content']) {
        if (node[key]) {
          node[key] = this.tightenPdfMakeContent(node[key], lineHeight, margin);
        }
      }
    }

    return node;
  }

  private applyEmojiFontToNode(node: any): any {
    if (Array.isArray(node)) {
      return node.map((child) => this.applyEmojiFontToNode(child));
    }

    if (typeof node === 'string') {
      return this.builder.make(node);
    }

    if (typeof node !== 'object' || node === null) {
      return node;
    }

    if (typeof node.text === 'string' && node.text.length > 0) {
      const baseStyle: any = {};
      for (const key of this.emojiStyleKeys) {
        if (typeof node[key] !== 'undefined') {
          baseStyle[key] = node[key];
        }
      }
      node.text = this.builder.make(node.text, baseStyle);
    } else if (Array.isArray(node.text)) {
      node.text = node.text.map((child: any) => this.applyEmojiFontToNode(child));
    }

    if (node.table && Array.isArray(node.table.body)) {
      node.table.body = node.table.body.map((row: any[]) => row.map((cell: any) => this.applyEmojiFontToNode(cell)));
    }

    for (const key of ['stack', 'columns', 'ul', 'ol', 'content']) {
      if (node[key]) {
        node[key] = this.applyEmojiFontToNode(node[key]);
      }
    }

    return node;
  }
}
